<template>
	<div class="rs-page">
		<!-- 无权限保护：仅业务员/管理员可见 -->
		<div v-if="!canAccess" class="rs-forbidden">
			<i class="el-icon-lock"></i>
			<div class="fb-title">无权访问</div>
			<div class="fb-tip">报告分享仅对业务员角色开放。</div>
		</div>

		<template v-else>
			<div class="rs-toolbar">
				<span class="rs-title">报告分享</span>
				<div class="rs-actions">
					<span class="rs-code" v-if="shareCode">我的分享编号：{{ shareCode }}</span>
					<el-button type="text" icon="el-icon-setting" @click="openWatermark">水印设置</el-button>
					<el-button type="text" icon="el-icon-chat-dot-round" @click="openWechat">微信号设置</el-button>
					<el-button type="text" icon="el-icon-message" @click="openSender">发件人设置</el-button>
					<el-button type="text" icon="el-icon-link" @click="openDomain">主域名设置</el-button>
					<el-button v-if="canManage" type="primary" size="small" icon="el-icon-folder-opened"
						@click="openManage">管理报告</el-button>
					<el-button size="small" icon="el-icon-refresh" @click="loadList">刷新</el-button>
				</div>
			</div>

			<div v-loading="loading" class="rs-body">
				<div v-if="!reports.length" class="rs-empty">
					暂无可分享的报告，请联系管理员在后台上传报告。
				</div>
				<div v-else class="rs-grid">
					<div class="rs-card" v-for="r in reports" :key="r.id">
						<div class="rs-cover" :style="coverStyle(r.coverUrl)">
							<div v-if="!r.coverUrl" class="rs-cover-ph"><i class="el-icon-document"></i></div>
						</div>
						<div class="rs-info">
							<div class="rs-name" :title="r.title">{{ r.title }}</div>
							<div class="rs-summary">{{ summaryBrief(r.summary) }}</div>
							<div class="rs-link">
								<el-input size="mini" readonly :value="r.shareUrl"></el-input>
								<el-button type="primary" size="mini" icon="el-icon-document-copy"
									@click="copyLink(r.shareUrl)">复制我的专属链接</el-button>
								<el-button size="mini" icon="el-icon-picture-outline"
									@click="openQrcode(r)">生成二维码</el-button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- 链接追踪板块 -->
			<div class="rs-track-section">
				<div class="rs-track-toolbar">
					<div class="rs-track-tabs">
						<span class="rs-track-tab" :class="{active: trackTab === 'mine'}" @click="switchTrackTab('mine')">我的链接</span>
						<span v-if="isAdmin" class="rs-track-tab" :class="{active: trackTab === 'admin'}" @click="switchTrackTab('admin')">业务员链接</span>
					</div>
					<div class="rs-track-actions">
						<template v-if="trackTab === 'mine'">
							<el-button type="primary" size="small" icon="el-icon-plus" @click="openTrackDialog()">新建链接</el-button>
							<el-button size="small" icon="el-icon-monitor" @click="openDeviceDialog">设备库</el-button>
							<el-button size="small" icon="el-icon-refresh" @click="loadTrackLinks">刷新</el-button>
						</template>
						<template v-else>
							<el-select v-model="adminTrackFilter" placeholder="全部业务员" size="small" clearable
								style="width:160px" @change="loadAdminTrackLinks">
								<el-option v-for="s in adminSalespersons" :key="s.id" :label="s.nickName || s.userName" :value="s.id"></el-option>
							</el-select>
							<el-button size="small" icon="el-icon-refresh" @click="loadAdminTrackLinks">刷新</el-button>
						</template>
					</div>
				</div>
				<!-- 我的链接表格 -->
				<el-table v-if="trackTab === 'mine'" v-loading="trackLoading" :data="trackLinks" size="small" border style="width:100%">
					<el-table-column label="链接地址" min-width="200">
						<template slot-scope="{ row }">
							<span class="rs-track-url">{{ row.fullUrl }}</span>
						</template>
					</el-table-column>
					<el-table-column label="微信号" min-width="120">
						<template slot-scope="{ row }">
							<el-input v-model="row.wechatId" size="mini" placeholder="点击填写"
								@change="saveTrackWechat(row)"></el-input>
						</template>
					</el-table-column>
					<el-table-column prop="targetUrl" label="目标URL" min-width="160" show-overflow-tooltip></el-table-column>
					<el-table-column prop="visitCount" label="访问次数" width="70" align="center">
						<template slot-scope="{ row }">
							<span :class="row.visitCount > 0 ? 'rs-track-hit' : ''">{{ row.visitCount }}</span>
						</template>
					</el-table-column>
					<el-table-column label="IP" width="120">
						<template slot-scope="{ row }">{{ row.lastVisitIp || '-' }}</template>
					</el-table-column>
					<el-table-column label="地区" width="90" show-overflow-tooltip>
						<template slot-scope="{ row }">{{ row.lastVisitRegion || '-' }}</template>
					</el-table-column>
					<el-table-column label="设备" min-width="150" show-overflow-tooltip>
						<template slot-scope="{ row }">{{ row.lastVisitDeviceInfo || '-' }}</template>
					</el-table-column>
					<el-table-column label="最近访问" width="140">
						<template slot-scope="{ row }">{{ fmtVisitTime(row.lastVisitTime) }}</template>
					</el-table-column>
					<el-table-column label="状态" width="60" align="center">
						<template slot-scope="{ row }">
							<el-tag size="mini" :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '启用' : '停用' }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column label="操作" width="320" align="center">
						<template slot-scope="{ row }">
							<el-button type="text" size="mini" @click="openTrackQrcode(row.id)">二维码</el-button>
							<el-button type="text" size="mini" @click="openVisitDialog(row)">访问</el-button>
							<el-button type="text" size="mini" @click="openHeatmapDialog(row)">热力图</el-button>
							<el-button type="text" size="mini" @click="copyTrackUrl(row.fullUrl)">复制</el-button>
							<el-button type="text" size="mini" @click="openTrackDialog(row)">编辑</el-button>
							<el-button type="text" size="mini" class="rs-mg-danger" @click="deleteTrackLink(row)">删除</el-button>
						</template>
					</el-table-column>
				</el-table>
				<div class="rs-track-tip" v-if="trackTab === 'mine'">· 自定义追踪链接格式：<code>https://heimingdan.im.linshin.cn/t/你的尾巴</code>，客户打开后自动跳转到目标URL并记录访问信息。</div>
				<!-- 业务员链接表格(管理员) -->
				<el-table v-if="trackTab === 'admin'" v-loading="adminTrackLoading" :data="adminTrackLinks" size="small" border style="width:100%">
					<el-table-column prop="nickName" label="业务员" width="90"></el-table-column>
					<el-table-column label="链接地址" min-width="200">
						<template slot-scope="{ row }"><span class="rs-track-url">{{ row.fullUrl }}</span></template>
					</el-table-column>
					<el-table-column prop="remark" label="备注" width="80" show-overflow-tooltip></el-table-column>
					<el-table-column prop="wechatId" label="微信号" width="100" show-overflow-tooltip></el-table-column>
					<el-table-column prop="targetUrl" label="目标URL" min-width="160" show-overflow-tooltip></el-table-column>
					<el-table-column prop="visitCount" label="访问" width="55" align="center">
						<template slot-scope="{ row }"><span :class="row.visitCount > 0 ? 'rs-track-hit' : ''">{{ row.visitCount }}</span></template>
					</el-table-column>
					<el-table-column label="IP" width="120">
						<template slot-scope="{ row }">{{ row.lastVisitIp || '-' }}</template>
					</el-table-column>
					<el-table-column label="地区" width="90" show-overflow-tooltip>
						<template slot-scope="{ row }">{{ row.lastVisitRegion || '-' }}</template>
					</el-table-column>
					<el-table-column label="设备" min-width="150" show-overflow-tooltip>
						<template slot-scope="{ row }">{{ row.lastVisitDeviceInfo || '-' }}</template>
					</el-table-column>
					<el-table-column label="最近访问" width="140">
						<template slot-scope="{ row }">{{ fmtVisitTime(row.lastVisitTime) }}</template>
					</el-table-column>
					<el-table-column label="状态" width="70" align="center">
						<template slot-scope="{ row }">
							<el-tag v-if="row.deleted" size="mini" type="danger">已删除</el-tag>
							<el-tag v-else size="mini" :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '启用' : '停用' }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column label="操作" width="140" align="center">
						<template slot-scope="{ row }">
							<el-button type="text" size="mini" @click="openAdminVisitDialog(row)">访问明细</el-button>
							<el-button v-if="row.deleted" type="text" size="mini" class="rs-mg-danger" @click="deleteAdminTrackLink(row)">删除</el-button>
						</template>
					</el-table-column>
				</el-table>
				<div class="rs-track-tip" v-if="trackTab === 'admin'">· 管理员可查看所有业务员创建的追踪链接及其访问数据。选择业务员可筛选其创建的链接。</div>
			</div>

			<!-- 新建/编辑追踪链接弹窗 -->
			<el-dialog :title="trackDialog.editing ? '编辑追踪链接' : '新建追踪链接'" :visible.sync="trackDialog.visible" width="480px" append-to-body>
				<el-form label-width="90px" size="small" @submit.native.prevent>
					<el-form-item label="自定义尾巴" required>
						<el-input v-model="trackDialog.slug" :disabled="!!trackDialog.editing" maxlength="32"
							placeholder="2-32位字母数字下划线短横线" style="width:200px"></el-input>
						<div class="rs-track-preview" v-if="trackDialog.slug">
							预览：https://heimingdan.im.linshin.cn/t/{{ trackDialog.slug }}
						</div>
					</el-form-item>
					<el-form-item label="目标URL" required>
						<el-input v-model="trackDialog.targetUrl" maxlength="500" type="textarea" :rows="2"
							placeholder="客户打开追踪链接后跳转到的网址"></el-input>
					</el-form-item>
					<el-form-item label="微信号">
						<el-input v-model="trackDialog.wechatId" maxlength="64" show-word-limit
							placeholder="给这条链接关联微信号(选填)"></el-input>
					</el-form-item>
				</el-form>
				<span slot="footer">
					<el-button size="small" @click="trackDialog.visible = false">取消</el-button>
					<el-button type="primary" size="small" :loading="trackSaving" @click="saveTrackLink">保存</el-button>
				</span>
			</el-dialog>

			<!-- 访问明细弹窗 -->
			<el-dialog title="访问明细" :visible.sync="visitDialog.visible" width="860px" append-to-body>
				<div v-loading="visitDialog.loading" style="min-height:200px">
					<div class="rs-visit-summary" v-if="visitDialog.linkSlug">
						链接：<code>https://heimingdan.im.linshin.cn/t/{{ visitDialog.linkSlug }}</code>
						<span class="rs-visit-stats" v-if="visitDialog.totalVisits">
							共 <b>{{ visitDialog.totalVisits }}</b> 次访问 · <b>{{ visitDialog.uniqueVisitors }}</b> 位访客
						</span>
					</div>
					<!-- 访客列表(点击行展开查看每次访问时间) -->
					<div v-if="visitDialog.visitors.length" class="rs-visitor-section">
						<el-table :data="visitDialog.visitors" size="mini" border style="width:100%" row-key="num">
							<el-table-column type="expand">
								<template slot-scope="{ row }">
									<div class="rs-visit-expand">
										<div v-if="row.ipList && row.ipList.length > 1" class="rs-visit-expand-ips">
											<span class="rs-visit-expand-label">所有IP：</span>
											<el-tag v-for="(ip, i) in row.ipList" :key="i" size="mini" type="info" style="margin-right:4px">{{ ip }}</el-tag>
										</div>
										<div>
											<span class="rs-visit-expand-label">访问时间线：</span>
											<span v-for="(v, i) in visitsByVisitor(row)" :key="i" class="rs-visit-time-tag">
												{{ fmtVisitTime(v.visitTime) }}
											</span>
										</div>
									</div>
								</template>
							</el-table-column>
							<el-table-column prop="num" label="#" width="40" align="center"></el-table-column>
							<el-table-column label="标记" width="90" align="center">
								<template slot-scope="{ row }">
									<el-tag v-if="row.deviceMark" :type="markTagType(row.deviceMark)" size="mini" effect="dark">{{ row.deviceMark }}</el-tag>
									<span v-else class="rs-visit-nomark">-</span>
								</template>
							</el-table-column>
							<el-table-column label="IP" width="130">
								<template slot-scope="{ row }">
									<span>{{ row.ip }}</span>
									<span v-if="row.ipList && row.ipList.length > 1" class="rs-visit-ip-count">(+{{ row.ipList.length - 1 }})</span>
								</template>
							</el-table-column>
							<el-table-column prop="deviceInfo" label="设备/浏览器" min-width="160" show-overflow-tooltip></el-table-column>
							<el-table-column label="真实性" width="75" align="center">
								<template slot-scope="{ row }">
									<el-tooltip v-if="row.deviceAuth && row.deviceAuth.indexOf(':') > 0" :content="row.deviceAuth.substring(row.deviceAuth.indexOf(':') + 1)" placement="top">
										<el-tag :type="deviceAuthTagType(row.deviceAuth)" size="mini" effect="dark">{{ deviceAuthLabel(row.deviceAuth) }}</el-tag>
									</el-tooltip>
									<el-tag v-else-if="row.deviceAuth" :type="deviceAuthTagType(row.deviceAuth)" size="mini" effect="dark">{{ deviceAuthLabel(row.deviceAuth) }}</el-tag>
									<span v-else class="rs-visit-nomark">-</span>
								</template>
							</el-table-column>
							<el-table-column prop="region" label="地区" width="90" show-overflow-tooltip></el-table-column>
							<el-table-column prop="visitCount" label="打开次数" width="70" align="center">
								<template slot-scope="{ row }"><span :class="row.visitCount > 1 ? 'rs-visit-multi' : ''">{{ row.visitCount }} 次</span></template>
							</el-table-column>
							<el-table-column label="最近访问" width="140">
								<template slot-scope="{ row }">{{ fmtVisitTime(row.lastVisit) }}</template>
							</el-table-column>
						</el-table>
					</div>
					<div v-if="!visitDialog.visitors.length && !visitDialog.loading" class="rs-track-empty">暂无访问记录</div>
				</div>
				<span slot="footer">
					<el-button size="small" @click="visitDialog.visible = false">关闭</el-button>
				</span>
			</el-dialog>

			<!-- 设备库弹窗(按IP折叠) -->
			<el-dialog title="设备库" :visible.sync="deviceDialog.visible" width="820px" append-to-body>
				<div v-loading="deviceDialog.loading" style="min-height:200px">
					<el-table v-if="deviceDialog.devices.length" :data="deviceGroups" size="mini" border style="width:100%">
						<el-table-column type="expand">
							<template slot-scope="{ row }">
								<el-table :data="row.devices" size="mini" border style="width:100%" :show-header="true" header-cell-class-name="rs-dev-sub-header">
									<el-table-column label="设备" min-width="200" show-overflow-tooltip>
										<template slot-scope="{ row: d }">{{ deviceIcon(d) }} {{ d.deviceInfo || '未知设备' }}</template>
									</el-table-column>
									<el-table-column label="访问" width="55" align="center">
										<template slot-scope="{ row: d }"><span :class="d.visitCount > 1 ? 'rs-visit-multi' : ''">{{ d.visitCount }}</span></template>
									</el-table-column>
									<el-table-column label="最近访问" width="135">
										<template slot-scope="{ row: d }">{{ fmtVisitTime(d.lastSeen) }}</template>
									</el-table-column>
									<el-table-column label="标记" width="70" align="center">
										<template slot-scope="{ row: d }">
											<el-tag v-if="d.mark" :type="markTagType(d.mark)" size="mini" effect="dark">{{ d.mark }}</el-tag>
											<span v-else class="rs-visit-nomark">-</span>
										</template>
									</el-table-column>
									<el-table-column label="操作" width="100" align="center">
										<template slot-scope="{ row: d }">
											<el-button size="mini" type="text" @click="openUaDialog(d)">详情</el-button>
											<el-button size="mini" type="text" :class="d.mark ? 'rs-mark-edit' : ''" @click="openMarkDialog(d)">{{ d.mark ? '改标记' : '标记' }}</el-button>
										</template>
									</el-table-column>
								</el-table>
							</template>
						</el-table-column>
						<el-table-column prop="ip" label="IP" width="130"></el-table-column>
						<el-table-column prop="region" label="地区" width="90" show-overflow-tooltip></el-table-column>
						<el-table-column label="设备" width="60" align="center">
							<template slot-scope="{ row }">{{ row.devices.length }} 台</template>
						</el-table-column>
						<el-table-column label="访问" width="55" align="center">
							<template slot-scope="{ row }"><span :class="row.totalVisits > 1 ? 'rs-visit-multi' : ''">{{ row.totalVisits }}</span></template>
						</el-table-column>
						<el-table-column label="最近访问" width="135">
							<template slot-scope="{ row }">{{ fmtVisitTime(row.lastSeen) }}</template>
						</el-table-column>
						<el-table-column label="标记" width="100" align="center">
							<template slot-scope="{ row }">
								<el-tag v-for="m in row.marks" :key="m" :type="markTagType(m)" size="mini" effect="dark" style="margin-right:2px">{{ m }}</el-tag>
								<span v-if="!row.marks.length" class="rs-visit-nomark">-</span>
							</template>
						</el-table-column>
					</el-table>
					<div v-if="!deviceDialog.loading && !deviceDialog.devices.length" class="rs-track-empty">暂无设备数据</div>
				</div>
				<span slot="footer">
					<el-button size="small" @click="deviceDialog.visible = false">关闭</el-button>
				</span>
			</el-dialog>

			<!-- UA详情弹窗 -->
			<el-dialog title="设备详情" :visible.sync="uaDialog.visible" width="680px" append-to-body custom-class="rs-ua-dialog">
				<div v-if="uaDialog.device" class="rs-ua-body">
					<div class="rs-ua-grid">
						<div class="rs-ua-cell">
							<div class="rs-ua-label">设备</div>
							<div class="rs-ua-value">{{ uaDialog.device.deviceInfo || '未知设备' }}</div>
						</div>
						<div class="rs-ua-cell">
							<div class="rs-ua-label">IP</div>
							<div class="rs-ua-value rs-ua-mono">{{ uaDialog.device.ip }}</div>
						</div>
						<div class="rs-ua-cell">
							<div class="rs-ua-label">地区</div>
							<div class="rs-ua-value">{{ uaDialog.device.region || '-' }}</div>
						</div>
						<div class="rs-ua-cell">
							<div class="rs-ua-label">访问次数</div>
							<div class="rs-ua-value">{{ uaDialog.device.visitCount || 0 }} 次</div>
						</div>
						<div class="rs-ua-cell">
							<div class="rs-ua-label">首次访问</div>
							<div class="rs-ua-value">{{ fmtVisitTime(uaDialog.device.firstSeen) }}</div>
						</div>
						<div class="rs-ua-cell">
							<div class="rs-ua-label">最近访问</div>
							<div class="rs-ua-value">{{ fmtVisitTime(uaDialog.device.lastSeen) }}</div>
						</div>
						<div v-if="uaDialog.device.mark" class="rs-ua-cell">
							<div class="rs-ua-label">标记</div>
							<div class="rs-ua-value"><el-tag :type="markTagType(uaDialog.device.mark)" size="mini" effect="dark">{{ uaDialog.device.mark }}</el-tag></div>
						</div>
						<div v-if="uaDialog.device.remark" class="rs-ua-cell rs-ua-cell-wide">
							<div class="rs-ua-label">备注</div>
							<div class="rs-ua-value">{{ uaDialog.device.remark }}</div>
						</div>
					</div>
					<div class="rs-ua-raw">
						<div class="rs-ua-raw-header">
							<span class="rs-ua-raw-title">User-Agent</span>
							<el-button size="mini" type="text" icon="el-icon-document-copy" @click="copyUa(uaDialog.device.userAgent)">复制</el-button>
						</div>
						<div class="rs-ua-raw-text">{{ uaDialog.device.userAgent || '(无)' }}</div>
					</div>
				</div>
				<span slot="footer">
					<el-button size="small" @click="uaDialog.visible = false">关闭</el-button>
				</span>
			</el-dialog>

			<!-- 设备标记弹窗 -->
			<el-dialog title="标记设备" :visible.sync="markDialog.visible" width="420px" append-to-body>
				<div class="rs-mark-form">
					<div class="rs-mark-presets">
						<el-button v-for="p in markPresets" :key="p" size="small"
							:type="markDialog.mark === p ? 'danger' : 'default'"
							@click="markDialog.mark = p">{{ p }}</el-button>
					</div>
					<el-input v-model="markDialog.mark" placeholder="或输入自定义标记" size="small" style="margin-top:12px"></el-input>
					<el-input v-model="markDialog.remark" type="textarea" :rows="2" placeholder="备注（可选）" size="small" style="margin-top:8px"></el-input>
				</div>
				<span slot="footer">
					<el-button size="small" @click="markDialog.visible = false">取消</el-button>
					<el-button size="small" type="primary" :loading="markDialog.loading" @click="saveMark">保存</el-button>
				</span>
			</el-dialog>

			<!-- 热力图弹窗 -->
			<el-dialog title="页面热力图" :visible.sync="heatmapDialog.visible" width="950px" append-to-body>
				<div v-loading="heatmapDialog.loading" style="min-height:200px">
					<div class="rs-heatmap-toolbar" v-if="heatmapDialog.totalSessions !== null">
						<span>会话 <b>{{ heatmapDialog.totalSessions }}</b></span>
						<span>点击 <b>{{ heatmapDialog.totalClicks }}</b></span>
						<span>停留 <b>{{ formatDwell(heatmapDialog.maxDwell) }}</b></span>
						<span>滚动 <b>{{ heatmapDialog.maxScroll }}px</b></span>
						<span class="rs-heatmap-legend">
							<span class="rs-heatmap-legend-hot"></span>点击
							<span class="rs-heatmap-legend-fold"></span>首屏
							<span class="rs-heatmap-legend-scroll"></span>最大滚动
						</span>
					</div>
					<div v-if="heatmapDialog.clicks.length" class="rs-heatmap-scroll">
						<div class="rs-heatmap-page" :style="{ width: heatmapDialog.viewportW + 'px', height: heatmapDialog.pageH + 'px' }">
							<iframe :src="heatmapDialog.targetUrl" class="rs-heatmap-iframe" scrolling="no" frameborder="0"></iframe>
							<div class="rs-heatmap-overlay">
								<div class="rs-heatmap-fold" :style="{ top: (heatmapDialog.viewportH || 667) + 'px' }"></div>
								<div v-if="heatmapDialog.maxScroll" class="rs-heatmap-scrollline" :style="{ top: (heatmapDialog.maxScroll + (heatmapDialog.viewportH || 667)) + 'px' }"></div>
								<div v-for="(c, i) in heatmapDialog.clicks" :key="i"
									 class="rs-heatmap-hot"
									 :style="{ left: c.x + 'px', top: c.y + 'px' }"
									 :title="c.tag + (c.text ? ': ' + c.text : '')">
									<span class="rs-heatmap-num">{{ i + 1 }}</span>
								</div>
							</div>
						</div>
					</div>
					<div v-if="!heatmapDialog.loading && heatmapDialog.totalSessions === 0" class="rs-track-empty">
						暂无热力图数据（用户需通过追踪链接访问报告页后才有数据）
					</div>
				</div>
				<span slot="footer">
					<el-button size="small" @click="heatmapDialog.visible = false">关闭</el-button>
				</span>
			</el-dialog>

			<!-- 水印设置弹窗 -->
			<el-dialog title="我的水印设置" :visible.sync="watermarkDialog" width="420px" append-to-body>
				<div class="rs-wm-tip">
					水印将加盖在分享落地页与发送给客户的 PDF 上。留空则默认使用您的昵称。
				</div>
				<el-input v-model="watermarkText" maxlength="64" show-word-limit
					placeholder="例如：张三 138****8888" size="small"></el-input>
				<div class="rs-wm-current">当前生效文案：{{ effectiveWatermark || '(昵称)' }}</div>
				<span slot="footer">
					<el-button size="small" @click="watermarkDialog = false">取消</el-button>
					<el-button type="primary" size="small" :loading="savingWatermark"
						@click="saveWatermark">保存</el-button>
				</span>
			</el-dialog>

			<!-- 微信号设置弹窗 -->
			<el-dialog title="我的微信号" :visible.sync="wechatDialog" width="420px" append-to-body>
				<div class="rs-wm-tip">
					客户领取报告后，邮件内会展示您的微信号，引导客户添加您。留空则邮件内不展示。
				</div>
				<el-input v-model="wechatId" maxlength="64" show-word-limit
					placeholder="例如：wx123456" size="small"></el-input>
				<span slot="footer">
					<el-button size="small" @click="wechatDialog = false">取消</el-button>
					<el-button type="primary" size="small" :loading="savingWechat"
						@click="saveWechat">保存</el-button>
				</span>
			</el-dialog>

			<!-- 发件人名称设置弹窗 -->
			<el-dialog title="邮件发件人名称" :visible.sync="senderDialog" width="420px" append-to-body>
				<div class="rs-wm-tip">
					客户收到报告邮件时看到的发件人名称。留空则使用默认的“AI行情分析”。
				</div>
				<el-input v-model="senderName" maxlength="32" show-word-limit
					placeholder="默认：AI行情分析" size="small"></el-input>
				<span slot="footer">
					<el-button size="small" @click="senderDialog = false">取消</el-button>
					<el-button type="primary" size="small" :loading="savingSender"
						@click="saveSender">保存</el-button>
				</span>
			</el-dialog>

			<!-- 主域名申请弹窗 -->
			<el-dialog title="我的专属主域名" :visible.sync="domainDialog" width="460px" append-to-body>
				<div class="rs-wm-tip">
					提交您想使用的主域名，管理员审核并配置解析后，您的分享链接会自动换成该域名。
				</div>
				<div class="rs-dm-row">当前生效域名：
					<span v-if="currentDomain" class="rs-dm-ok">{{ currentDomain }}</span>
					<span v-else class="rs-dm-none">未配置（使用主站域名）</span>
				</div>
				<div v-if="applyStatus === 1" class="rs-dm-row">
					申请状态：<el-tag size="mini" type="warning">待审核</el-tag>
					<span class="rs-dm-apply">{{ applyDomain }}</span>
				</div>
				<div v-else-if="applyStatus === 3" class="rs-dm-row">
					申请状态：<el-tag size="mini" type="danger">已驳回</el-tag>
					<span class="rs-dm-apply">{{ applyDomain }}</span>
					<span v-if="applyReason" class="rs-dm-reason">原因：{{ applyReason }}</span>
				</div>
				<el-input v-model="domainInput" maxlength="100"
					placeholder="例：share.example.com" size="small"></el-input>
				<span slot="footer">
					<el-button size="small" @click="domainDialog = false">取消</el-button>
					<el-button type="primary" size="small" :loading="savingDomain"
						@click="saveDomainApply">提交申请</el-button>
				</span>
			</el-dialog>

			<!-- 二维码弹窗(报告分享/追踪链接共用) -->
			<el-dialog title="二维码" :visible.sync="qrcodeDialog" width="360px"
				custom-class="rs-qr-dialog" append-to-body>
				<div v-loading="qrLoading" class="rs-qr-box">
					<div class="rs-qr-frame">
						<img v-if="qrImage" :src="qrImage" alt="二维码" />
					</div>
					<div class="rs-qr-name" :title="qrTitle">{{ qrTitle }}</div>
					<div class="rs-qr-tip">
						<i class="el-icon-info"></i>
						{{ qrTip }}
					</div>
					<el-button class="rs-qr-download" type="primary" icon="el-icon-download"
						:disabled="!qrImage" @click="downloadQr">下载二维码图片</el-button>
				</div>
			</el-dialog>

			<!-- 报告管理弹窗(需后台开通工作台管理权) -->
			<el-dialog title="报告管理" :visible.sync="manageDialog" width="880px"
				custom-class="rs-manage-dialog" append-to-body>
				<div class="rs-mg-toolbar">
					<el-button type="primary" size="small" icon="el-icon-plus" @click="openEdit()">上传报告</el-button>
					<el-button size="small" icon="el-icon-refresh" @click="loadManageList">刷新</el-button>
				</div>
				<el-table v-loading="manageLoading" :data="manageList" size="small" border>
					<el-table-column label="封面" width="70" align="center">
						<template slot-scope="{ row }">
							<img v-if="row.coverUrl" :src="row.coverUrl" class="rs-mg-thumb" />
							<i v-else class="el-icon-document"></i>
						</template>
					</el-table-column>
					<el-table-column prop="title" label="标题" min-width="150" show-overflow-tooltip></el-table-column>
					<el-table-column prop="summary" label="摘要" min-width="180" show-overflow-tooltip></el-table-column>
					<el-table-column prop="sort" label="排序" width="60" align="center"></el-table-column>
					<el-table-column label="状态" width="70" align="center">
						<template slot-scope="{ row }">
							<el-tag size="mini" :type="row.enabled ? 'success' : 'info'">
								{{ row.enabled ? '启用' : '停用' }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column label="操作" width="170" align="center">
						<template slot-scope="{ row }">
							<el-button type="text" size="mini" @click="openEdit(row)">编辑</el-button>
							<el-button type="text" size="mini" @click="toggleReport(row)">
								{{ row.enabled ? '停用' : '启用' }}</el-button>
							<el-button type="text" size="mini" class="rs-mg-danger"
								@click="deleteReport(row)">删除</el-button>
						</template>
					</el-table-column>
				</el-table>
				<div class="rs-wm-tip" style="margin: 10px 0 0;">
					· 保存后对所有业务员生效，标题/摘要/封面就是客户打开分享页看到的内容。<br />
					· 停用的报告不会展示给业务员，也无法被客户领取。
				</div>
			</el-dialog>

			<!-- 报告编辑弹窗 -->
			<el-dialog :title="editForm.id ? '编辑报告' : '上传报告'" :visible.sync="editDialog" width="560px"
				custom-class="rs-edit-dialog" append-to-body>
				<el-form label-width="90px" size="small" @submit.native.prevent>
					<el-form-item label="标题" required>
						<el-input v-model="editForm.title" maxlength="128" placeholder="客户看到的报告标题"></el-input>
					</el-form-item>
					<el-form-item label="摘要">
						<el-input v-model="editForm.summary" type="textarea" :rows="3" maxlength="512"
							placeholder="分享页展示的简介(选填)"></el-input>
					</el-form-item>
					<el-form-item label="PDF文件" required>
						<el-button size="small" icon="el-icon-upload2" :loading="uploadingPdf" @click="pickPdf">
							{{ editForm.pdfUrl ? '重新上传PDF' : '上传PDF' }}</el-button>
						<span v-if="editForm.pdfUrl" class="rs-edit-ok"><i class="el-icon-success"></i> 已上传</span>
						<input ref="pdfInput" type="file" accept="application/pdf" style="display:none"
							@change="onPdfChange" />
					</el-form-item>
					<el-form-item label="预览图">
						<div class="rs-edit-preview-row">
							<el-button size="small" type="primary" :loading="parseLoading" :disabled="!editForm.pdfUrl"
								@click="parseAllPages">解析全部页面</el-button>
							<el-button size="small" v-if="selectedPages.length" @click="clearPreviewImgs">清空已选</el-button>
							<span v-if="selectedPages.length" class="rs-wm-tip" style="margin-left:8px">已选 {{ selectedPages.length }} 页</span>
						</div>
						<div v-if="allPages.length" style="max-height:260px;overflow-y:auto;border:1px solid #3a3f55;border-radius:8px;padding:8px;margin-bottom:8px">
							<div v-for="p in allPages" :key="p.page" @click="togglePage(p.page)"
								style="position:relative;cursor:pointer;width:calc(25% - 6px);margin:3px;display:inline-block;vertical-align:top">
								<img :src="p.url" style="width:100%;border-radius:4px;display:block"
									:style="{ border: selectedSet[p.page] ? '2px solid #5fd38a' : '2px solid rgba(255,255,255,0.1)' }" />
								<div :style="{ position:'absolute',top:'2px',left:'2px',width:'18px',height:'18px',borderRadius:'3px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',color:'#fff', background: selectedSet[p.page] ? '#5fd38a' : 'rgba(0,0,0,0.5)' }">{{ selectedSet[p.page] ? '✓' : '' }}</div>
								<div style="text-align:center;font-size:11px;color:rgba(255,255,255,0.5);margin-top:2px">第{{p.page}}页</div>
							</div>
						</div>
						<div class="rs-wm-tip" style="margin: 6px 0 0;">
							解析PDF全部页后，点击缩略图勾选要展示的页面。已选页保存后在分享页展示。
						</div>
					</el-form-item>
					<el-form-item label="封面图">
						<el-button size="small" icon="el-icon-picture-outline" :loading="uploadingCover"
							@click="pickCover">{{ editForm.coverUrl ? '重新上传封面' : '上传封面' }}</el-button>
						<input ref="coverInput" type="file" accept="image/*" style="display:none"
							@change="onCoverChange" />
						<img v-if="editForm.coverUrl" :src="editForm.coverUrl" class="rs-edit-img rs-edit-cover" />
					</el-form-item>
					<el-form-item label="排序">
						<el-input-number v-model="editForm.sort" :min="0" :max="9999" size="small"
							controls-position="right" style="width:110px"></el-input-number>
					</el-form-item>
					<el-form-item label="启用">
						<el-switch v-model="editForm.enabled"></el-switch>
					</el-form-item>
				</el-form>
				<span slot="footer">
					<el-button size="small" @click="editDialog = false">取消</el-button>
					<el-button type="primary" size="small" :loading="editSaving" @click="saveEdit">保存</el-button>
				</span>
			</el-dialog>
		</template>
	</div>
</template>

<script>
import bizApi from '../api/bizApi.js';

export default {
	name: 'reportShare',
	data() {
		return {
			loading: false,
			reports: [],
			shareCode: '',
			effectiveWatermark: '',
			watermarkDialog: false,
			watermarkText: '',
			savingWatermark: false,
			wechatDialog: false,
			wechatId: '',
			savingWechat: false,
			senderDialog: false,
			senderName: '',
			savingSender: false,
			domainDialog: false,
			domainInput: '',
			currentDomain: '',
			applyDomain: '',
			applyStatus: 0,
			applyReason: '',
			savingDomain: false,
			qrcodeDialog: false,
			qrLoading: false,
			qrImage: '',
			qrTitle: '',
			qrTip: '',
			// 工作台报告管理(需后台开通)
			canManage: false,
			manageDialog: false,
			manageLoading: false,
			manageList: [],
			editDialog: false,
			editSaving: false,
			uploadingPdf: false,
			uploadingCover: false,
			genLoading: false,
			parseLoading: false,
			allPages: [],
			editForm: {
				id: null,
				title: '',
				summary: '',
				coverUrl: '',
				pdfUrl: '',
				previewImgUrl: '',
				previewPage: 1,
				previewImgs: '',
				sort: 0,
				enabled: true
			},
			// 链接追踪
			trackLinks: [],
			trackLoading: false,
			trackSaving: false,
			trackDialog: { visible: false, editing: null, slug: '', wechatId: '', targetUrl: '' },
			visitDialog: { visible: false, loading: false, linkSlug: '', visits: [], visitors: [], totalVisits: 0, uniqueVisitors: 0 },
			deviceDialog: { visible: false, loading: false, devices: [] },
			markDialog: { visible: false, loading: false, deviceId: null, mark: '', remark: '' },
			markPresets: ['诈骗设备', '可疑设备', '黑名单', '正常'],
			uaDialog: { visible: false, device: null },
			heatmapDialog: { visible: false, loading: false, linkId: null, linkSlug: '', targetUrl: '', totalSessions: null, totalClicks: 0, maxScroll: 0, maxDwell: 0, sessions: [], clicks: [], viewportW: 375, viewportH: 667, pageH: 2000 },
			// 链接追踪 tab
			trackTab: 'mine',
			// 管理员: 业务员链接列表
			adminTrackLinks: [],
			adminTrackLoading: false,
			adminTrackFilter: '',
			adminSalespersons: []
		};
	},
	computed: {
		canAccess() {
			return true;
		},
		selectedSet() {
			var set = {};
			try { JSON.parse(this.editForm.previewImgs || '[]').forEach(function(p) { set[p.page] = true; }); } catch(e) { /* ignore */ }
			return set;
		},
		selectedPages() {
			try { return JSON.parse(this.editForm.previewImgs || '[]'); } catch(e) { return []; }
		},
		isAdmin() {
			return this.userStore && this.userStore.isBizAdmin;
		},
		deviceGroups() {
			var map = {};
			for (var i = 0; i < this.deviceDialog.devices.length; i++) {
				var d = this.deviceDialog.devices[i];
				if (!map[d.ip]) map[d.ip] = { ip: d.ip, region: d.region || '', devices: [], totalVisits: 0, lastSeen: null, marks: [] };
				var g = map[d.ip];
				g.devices.push(d);
				g.totalVisits += (d.visitCount || 0);
				if (!g.lastSeen || (d.lastSeen && new Date(d.lastSeen) > new Date(g.lastSeen))) g.lastSeen = d.lastSeen;
				if (d.mark && d.mark !== '正常' && g.marks.indexOf(d.mark) < 0) g.marks.push(d.mark);
			}
			return Object.values(map).sort(function(a, b) { return new Date(b.lastSeen) - new Date(a.lastSeen); });
		}
	},
	methods: {
		async loadList() {
			if (!this.canAccess) return;
			this.loading = true;
			try {
				const data = await bizApi.reportList();
				this.reports = (data && data.reports) || [];
				this.shareCode = (data && data.shareCode) || '';
				this.effectiveWatermark = (data && data.watermarkText) || '';
			} catch (e) {
				this.reports = [];
			} finally {
				this.loading = false;
			}
		},
		async openWatermark() {
			try {
				const data = await bizApi.reportMyShare();
				this.watermarkText = (data && data.watermarkText) || '';
				this.effectiveWatermark = (data && data.effectiveWatermark) || '';
			} catch (e) {
				this.watermarkText = '';
			}
			this.watermarkDialog = true;
		},
		async saveWatermark() {
			if (this.savingWatermark) return;
			this.savingWatermark = true;
			try {
				await bizApi.reportUpdateWatermark(this.watermarkText);
				this.$message.success('保存成功');
				this.watermarkDialog = false;
				// 刷新生效文案与链接
				await this.loadList();
			} catch (e) {
				// http 拦截器已弹出错误提示
			} finally {
				this.savingWatermark = false;
			}
		},
		async openWechat() {
			try {
				const data = await bizApi.reportMyShare();
				this.wechatId = (data && data.wechatId) || '';
			} catch (e) {
				this.wechatId = '';
			}
			this.wechatDialog = true;
		},
		async saveWechat() {
			if (this.savingWechat) return;
			this.savingWechat = true;
			try {
				await bizApi.reportUpdateWechat(this.wechatId);
				this.$message.success('保存成功');
				this.wechatDialog = false;
			} catch (e) {
				// http 拦截器已弹出错误提示
			} finally {
				this.savingWechat = false;
			}
		},
		async openSender() {
			try {
				const data = await bizApi.reportMyShare();
				this.senderName = (data && data.senderName) || '';
			} catch (e) {
				this.senderName = '';
			}
			this.senderDialog = true;
		},
		async saveSender() {
			if (this.savingSender) return;
			this.savingSender = true;
			try {
				await bizApi.reportUpdateSender(this.senderName);
				this.$message.success('发件人名称已保存');
				this.senderDialog = false;
			} catch (e) {
				// http 拦截器已弹出错误提示
			} finally {
				this.savingSender = false;
			}
		},
		async openDomain() {
			try {
				const data = await bizApi.reportMyShare();
				this.currentDomain = (data && data.currentDomain) || '';
				this.applyDomain = (data && data.applyDomain) || '';
				this.applyStatus = (data && data.applyStatus) || 0;
				this.applyReason = (data && data.applyReason) || '';
				// 驳回后回填上次申请的域名便于修改重交
				this.domainInput = this.applyStatus === 3 ? this.applyDomain : '';
			} catch (e) {
				this.currentDomain = '';
				this.applyStatus = 0;
			}
			this.domainDialog = true;
		},
		async saveDomainApply() {
			if (this.savingDomain) return;
			const domain = (this.domainInput || '').trim();
			if (!domain) {
				this.$message.warning('请填写想使用的域名');
				return;
			}
			this.savingDomain = true;
			try {
				await bizApi.reportApplyDomain(domain);
				this.$message.success('申请已提交，等待管理员审核配置');
				this.domainDialog = false;
			} catch (e) {
				// http 拦截器已弹出错误提示;同时刷新弹窗状态,
				// 避免审核已通过但弹窗仍显示旧的"未配置"造成误解
				try {
					const data = await bizApi.reportMyShare();
					this.currentDomain = (data && data.currentDomain) || '';
					this.applyDomain = (data && data.applyDomain) || '';
					this.applyStatus = (data && data.applyStatus) || 0;
					this.applyReason = (data && data.applyReason) || '';
				} catch (e2) {
					// 忽略刷新失败
				}
			} finally {
				this.savingDomain = false;
			}
		},
		async openQrcode(report) {
			this.qrcodeDialog = true;
			this.qrLoading = true;
			this.qrImage = '';
			this.qrTitle = report.title || '';
			this.qrTip = '微信扫码打开您的专属落地页，可转发好友或分享到朋友圈';
			try {
				const data = await bizApi.reportQrcode(report.id);
				this.qrImage = (data && data.qrBase64) || '';
			} catch (e) {
				this.$message.error('二维码生成失败');
				this.qrcodeDialog = false;
			} finally {
				this.qrLoading = false;
			}
		},
		async openTrackQrcode(linkId) {
			this.qrcodeDialog = true;
			this.qrLoading = true;
			this.qrImage = '';
			this.qrTitle = '';
			this.qrTip = '微信扫码打开追踪链接，可截图保存后发朋友圈';
			try {
				const data = await bizApi.trackQrcode(linkId);
				this.qrImage = (data && data.qrBase64) || '';
				if (data && data.url) this.qrTitle = data.url;
			} catch (e) {
				this.$message.error('二维码生成失败');
				this.qrcodeDialog = false;
			} finally {
				this.qrLoading = false;
			}
		},
		downloadQr() {
			if (!this.qrImage) return;
			const a = document.createElement('a');
			a.href = this.qrImage;
			a.download = (this.qrTitle || '分享二维码') + '.png';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		},
		copyLink(link) {
			if (!link) return;
			const done = () => this.$message.success('链接已复制');
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(link).then(done).catch(() => this.fallbackCopy(link, done));
			} else {
				this.fallbackCopy(link, done);
			}
		},
		fallbackCopy(text, done) {
			try {
				const ta = document.createElement('textarea');
				ta.value = text;
				ta.style.position = 'fixed';
				ta.style.left = '-9999px';
				document.body.appendChild(ta);
				ta.select();
				document.execCommand('copy');
				document.body.removeChild(ta);
				done();
			} catch (e) {
				this.$message.warning('复制失败，请手动复制');
			}
		},
		coverStyle(url) {
			return url ? { backgroundImage: `url(${url})` } : {};
		},
		summaryBrief(summary) {
			if (!summary) return '暂无摘要';
			return summary.length > 60 ? summary.slice(0, 60) + '...' : summary;
		},
		// ---------- 工作台报告管理 ----------
		async loadPerm() {
			try {
				const data = await bizApi.reportManagePerm();
				this.canManage = !!(data && data.canManage);
			} catch (e) {
				this.canManage = false;
			}
		},
		openManage() {
			this.manageDialog = true;
			this.loadManageList();
		},
		async loadManageList() {
			this.manageLoading = true;
			try {
				const data = await bizApi.reportManageList();
				this.manageList = data || [];
			} catch (e) {
				this.manageList = [];
			} finally {
				this.manageLoading = false;
			}
		},
		openEdit(row) {
			this.allPages = [];
			if (row) {
				this.editForm = {
					id: row.id,
					title: row.title || '',
					summary: row.summary || '',
					coverUrl: row.coverUrl || '',
					pdfUrl: row.pdfUrl || '',
					previewImgUrl: row.previewImgUrl || '',
					previewPage: row.previewPage || 1,
					previewImgs: row.previewImgs || '',
					sort: row.sort || 0,
					enabled: !!row.enabled
				};
			} else {
				this.editForm = {
					id: null,
					title: '',
					summary: '',
					coverUrl: '',
					pdfUrl: '',
					previewImgUrl: '',
					previewPage: 1,
					previewImgs: '',
					sort: 0,
					enabled: true
				};
			}
			this.editDialog = true;
		},
		pickPdf() {
			this.$refs.pdfInput.click();
		},
		async onPdfChange(e) {
			const file = e.target.files && e.target.files[0];
			e.target.value = '';
			if (!file) return;
			this.uploadingPdf = true;
			try {
				const formData = new FormData();
				formData.append('file', file);
				const data = await bizApi.reportManageUploadFile(formData);
				this.editForm.pdfUrl = (data && data.url) || '';
				this.$message.success('PDF上传成功');
			} catch (err) {
				// http 拦截器已弹出错误提示
			} finally {
				this.uploadingPdf = false;
			}
		},
		pickCover() {
			this.$refs.coverInput.click();
		},
		async onCoverChange(e) {
			const file = e.target.files && e.target.files[0];
			e.target.value = '';
			if (!file) return;
			this.uploadingCover = true;
			try {
				const formData = new FormData();
				formData.append('file', file);
				const data = await bizApi.reportManageUploadImage(formData);
				this.editForm.coverUrl = (data && data.url) || '';
				this.$message.success('封面上传成功');
			} catch (err) {
				// http 拦截器已弹出错误提示
			} finally {
				this.uploadingCover = false;
			}
		},
		async parseAllPages() {
			if (!this.editForm.pdfUrl) {
				this.$message.warning('请先上传PDF文件');
				return;
			}
			this.parseLoading = true;
			try {
				const data = await bizApi.reportManagePdfAllPages(this.editForm.pdfUrl);
				this.allPages = (data && data.pages) || [];
				if (data && data.totalPages) {
					this.$message.success(`已解析 ${data.totalPages} 页`);
				}
			} catch (err) {
				// http 拦截器已弹出错误提示
			} finally {
				this.parseLoading = false;
			}
		},
		togglePage(page) {
			var sel = JSON.parse(JSON.stringify(this.selectedPages));
			var idx = -1;
			for (var i = 0; i < sel.length; i++) { if (sel[i].page === page) { idx = i; break; } }
			if (idx >= 0) { sel.splice(idx, 1); } else {
				var p = this.allPages.find(function(x) { return x.page === page; });
				if (p) sel.push({ page: p.page, url: p.url });
			}
			sel.sort(function(a, b) { return a.page - b.page; });
			this.editForm.previewImgs = sel.length ? JSON.stringify(sel) : '';
			if (sel.length) { this.editForm.previewImgUrl = sel[0].url; this.editForm.previewPage = sel[0].page; }
			else { this.editForm.previewImgUrl = ''; this.editForm.previewPage = null; }
		},
		clearPreviewImgs() {
			this.editForm.previewImgs = '';
			this.editForm.previewImgUrl = '';
			this.editForm.previewPage = null;
		},
		async saveEdit() {
			if (!this.editForm.title || !this.editForm.title.trim()) {
				this.$message.warning('请填写报告标题');
				return;
			}
			if (!this.editForm.pdfUrl) {
				this.$message.warning('请上传PDF文件');
				return;
			}
			if (this.editSaving) return;
			this.editSaving = true;
			try {
				await bizApi.reportManageSave({ ...this.editForm });
				this.$message.success('保存成功');
				this.editDialog = false;
				await this.loadManageList();
				await this.loadList();
			} catch (err) {
				// http 拦截器已弹出错误提示
			} finally {
				this.editSaving = false;
			}
		},
		async toggleReport(row) {
			try {
				await bizApi.reportManageToggle(row.id, !row.enabled);
				this.$message.success(row.enabled ? '已停用' : '已启用');
				await this.loadManageList();
				await this.loadList();
			} catch (err) {
				// http 拦截器已弹出错误提示
			}
		},
		deleteReport(row) {
			this.$confirm(`确定删除报告「${row.title}」吗？删除后客户将无法领取。`, '提示', {
				type: 'warning',
				confirmButtonText: '删除',
				cancelButtonText: '取消'
			}).then(async () => {
				try {
					await bizApi.reportManageDelete(row.id);
					this.$message.success('已删除');
					await this.loadManageList();
					await this.loadList();
				} catch (err) {
					// http 拦截器已弹出错误提示
				}
			}).catch(() => {});
		},
		// ---------- 链接追踪 ----------
		async loadTrackLinks() {
			this.trackLoading = true;
			try {
				this.trackLinks = await bizApi.trackLinks() || [];
			} catch (e) {
				this.trackLinks = [];
			} finally {
				this.trackLoading = false;
			}
		},
		openTrackDialog(link) {
			if (link) {
				this.trackDialog = {
					visible: true, editing: link.id,
					slug: link.slug || '', wechatId: link.wechatId || '', targetUrl: link.targetUrl || ''
				};
			} else {
				this.trackDialog = { visible: true, editing: null, slug: '', wechatId: '', targetUrl: '' };
			}
		},
		async saveTrackLink() {
			const slug = (this.trackDialog.slug || '').trim();
			const targetUrl = (this.trackDialog.targetUrl || '').trim();
			const wechatId = (this.trackDialog.wechatId || '').trim();
			if (!slug) { this.$message.warning('请填写自定义尾巴'); return; }
			if (!/^[A-Za-z0-9_-]{2,32}$/.test(slug)) { this.$message.warning('尾巴仅允许2-32位字母、数字、下划线、短横线'); return; }
			if (!targetUrl) { this.$message.warning('请填写目标跳转URL'); return; }
			if (!/^https?:\/\//.test(targetUrl)) { this.$message.warning('目标URL需以 http:// 或 https:// 开头'); return; }
			if (this.trackSaving) return;
			this.trackSaving = true;
			try {
				let linkId;
				if (this.trackDialog.editing) {
					await bizApi.trackUpdate(this.trackDialog.editing, { wechatId, targetUrl });
					linkId = this.trackDialog.editing;
					this.$message.success('修改成功');
				} else {
					const data = await bizApi.trackCreate({ slug, wechatId, targetUrl });
					linkId = data && data.id;
					this.$message.success('创建成功');
				}
				this.trackDialog.visible = false;
				await this.loadTrackLinks();
				// 保存成功后自动弹出二维码
				if (linkId) {
					this.openTrackQrcode(linkId);
				}
			} catch (e) {
				// http 拦截器已弹出错误提示
			} finally {
				this.trackSaving = false;
			}
		},
		async saveTrackWechat(row) {
			try {
				await bizApi.trackUpdate(row.id, { wechatId: row.wechatId || '' });
				this.$message.success('微信号已保存');
			} catch (e) {
				// http 拦截器已弹出错误提示
				await this.loadTrackLinks();
			}
		},
		deleteTrackLink(row) {
			this.$confirm(`确定删除追踪链接「${row.slug}」吗？访问记录将一并删除。`, '提示', {
				type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消'
			}).then(async () => {
				try {
					await bizApi.trackDelete(row.id);
					this.$message.success('已删除');
					await this.loadTrackLinks();
				} catch (e) {
					// http 拦截器已弹出错误提示
				}
			}).catch(() => {});
		},
		async openVisitDialog(row) {
			this.visitDialog = { visible: true, loading: true, linkSlug: row.slug, visits: [], visitors: [], totalVisits: 0, uniqueVisitors: 0 };
			try {
				const data = await bizApi.trackVisits(row.id);
				if (data) {
					this.visitDialog.visits = data.visits || [];
					this.visitDialog.visitors = data.visitors || [];
					this.visitDialog.totalVisits = data.totalVisits || 0;
					this.visitDialog.uniqueVisitors = data.uniqueVisitors || 0;
				}
			} catch (e) {
				this.visitDialog.visits = [];
			} finally {
				this.visitDialog.loading = false;
			}
		},
		// ---------- 链接追踪 tab 切换 ----------
		switchTrackTab(tab) {
			this.trackTab = tab;
			if (tab === 'admin') {
				this.loadAdminTrackLinks();
			}
		},
		async loadAdminTrackLinks() {
			this.adminTrackLoading = true;
			try {
				// 首次加载业务员下拉
				if (!this.adminSalespersons.length) {
					try {
					this.adminSalespersons = await bizApi.salespersons() || [];
					} catch (e) { /* ignore */ }
				}
				this.adminTrackLinks = await bizApi.trackAdminLinks(this.adminTrackFilter) || [];
			} catch (e) {
				this.adminTrackLinks = [];
				this.$message.error('加载失败');
			} finally {
				this.adminTrackLoading = false;
			}
		},
		async openAdminVisitDialog(row) {
			this.visitDialog = { visible: true, loading: true, linkSlug: row.slug, visits: [], visitors: [], totalVisits: 0, uniqueVisitors: 0 };
			try {
				const data = await bizApi.trackAdminVisits(row.id);
				if (data) {
					this.visitDialog.visits = data.visits || [];
					this.visitDialog.visitors = data.visitors || [];
					this.visitDialog.totalVisits = data.totalVisits || 0;
					this.visitDialog.uniqueVisitors = data.uniqueVisitors || 0;
				}
			} catch (e) {
				this.visitDialog.visits = [];
				this.$message.error('加载失败');
			} finally {
				this.visitDialog.loading = false;
			}
		},
		deleteAdminTrackLink(row) {
			this.$confirm(`确定彻底删除链接「${row.slug}」吗？此操作不可恢复，访问记录将一并删除。`, '提示', {
				type: 'warning', confirmButtonText: '彻底删除', cancelButtonText: '取消'
			}).then(async () => {
				try {
					await bizApi.trackAdminDelete(row.id);
					this.$message.success('已彻底删除');
					await this.loadAdminTrackLinks();
				} catch (e) {
					// http 拦截器已弹出错误提示
				}
			}).catch(() => {});
		},
		visitsByVisitor(row) {
			// 按UA指纹匹配(同一设备不同IP算同一访客)
			if (row.fp) {
				return this.visitDialog.visits.filter(function(v) { return v.fp === row.fp; });
			}
			return this.visitDialog.visits.filter(function(v) { return v.ip === row.ip && v.deviceInfo === row.deviceInfo; });
		},
		async openDeviceDialog() {
			this.deviceDialog = { visible: true, loading: true, devices: [] };
			try {
				this.deviceDialog.devices = await bizApi.trackDevices() || [];
			} catch (e) {
				this.deviceDialog.devices = [];
			} finally {
				this.deviceDialog.loading = false;
			}
		},
		copyTrackUrl(url) {
			this.copyLink(url);
		},
		openMarkDialog(d) {
			this.markDialog = { visible: true, loading: false, deviceId: d.id, mark: d.mark || '', remark: d.remark || '' };
		},
		openUaDialog(d) {
			this.uaDialog = { visible: true, device: d };
		},
		copyUa(ua) {
			if (!ua) return;
			var self = this;
			if (navigator.clipboard) {
				navigator.clipboard.writeText(ua).then(function() { self.$message.success('已复制'); });
			} else {
				var ta = document.createElement('textarea');
				ta.value = ua;
				document.body.appendChild(ta);
				ta.select();
				document.execCommand('copy');
				document.body.removeChild(ta);
				self.$message.success('已复制');
			}
		},
		async saveMark() {
			this.markDialog.loading = true;
			try {
				await bizApi.trackMarkDevice(this.markDialog.deviceId, this.markDialog.mark, this.markDialog.remark);
				// 同步更新卡片数据
				const dev = this.deviceDialog.devices.find(d => d.id === this.markDialog.deviceId);
				if (dev) {
					dev.mark = this.markDialog.mark;
					dev.remark = this.markDialog.remark;
				}
				this.markDialog.visible = false;
				this.$message.success('标记已保存');
			} catch (e) {
				this.$message.error('标记失败');
			} finally {
				this.markDialog.loading = false;
			}
		},
		isWarnMark(mark) {
			if (!mark || mark === '正常') return false;
			return true;
		},
		markTagType(mark) {
			if (!mark || mark === '正常') return 'success';
			if (mark.includes('诈骗') || mark.includes('黑名单')) return 'danger';
			return 'warning';
		},
		deviceAuthTagType(auth) {
			if (!auth) return 'info';
			var label = auth.split(':')[0];
			if (label === '伪造') return 'danger';
			if (label === '可疑') return 'warning';
			if (label === '真实') return 'success';
			return 'info';
		},
		deviceAuthLabel(auth) {
			if (!auth) return '';
			return auth.split(':')[0];
		},
		deviceIcon(d) {
			const ua = (d.userAgent || '').toLowerCase();
			if (ua.includes('iphone') || ua.includes('ipad')) return '🍎';
			if (ua.includes('android')) return '🤖';
			if (ua.includes('windows')) return '🖥';
			if (ua.includes('mac')) return '💻';
			return '📱';
		},
		fmtVisitTime(t) {
			if (!t) return '-';
			let d;
			if (typeof t === 'number') d = new Date(t);
			else if (typeof t === 'string') d = new Date(t);
			else d = t;
			if (!(d instanceof Date) || isNaN(d)) return '-';
			const p = (x) => String(x).padStart(2, '0');
			return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
		},
		async openHeatmapDialog(row) {
			this.heatmapDialog = {
				visible: true, loading: true, linkId: row.id, linkSlug: row.slug, targetUrl: row.targetUrl,
				totalSessions: null, totalClicks: 0, maxScroll: 0, maxDwell: 0,
				sessions: [], clicks: [], viewportW: 375, viewportH: 667, pageH: 2000
			};
			try {
				const data = await bizApi.trackHeatmap(row.id);
				if (data) {
					this.heatmapDialog.totalSessions = data.totalSessions || 0;
					this.heatmapDialog.totalClicks = data.totalClicks || 0;
					this.heatmapDialog.maxScroll = data.maxScroll || 0;
					this.heatmapDialog.maxDwell = data.maxDwell || 0;
					this.heatmapDialog.sessions = data.sessions || [];
					this.heatmapDialog.clicks = data.clicks || [];
					if (this.heatmapDialog.sessions.length > 0) {
						const s = this.heatmapDialog.sessions[0];
						this.heatmapDialog.viewportW = s.viewportW || 375;
						this.heatmapDialog.viewportH = s.viewportH || 667;
						this.heatmapDialog.pageH = s.pageH || 2000;
					}
				}
			} catch (e) {
				this.heatmapDialog.totalSessions = 0;
			} finally {
				this.heatmapDialog.loading = false;
			}
		},
		formatDwell(ms) {
			if (!ms || ms < 1000) return (ms || 0) + 'ms';
			const s = Math.floor(ms / 1000);
			if (s < 60) return s + '秒';
			const m = Math.floor(s / 60);
			return m + '分' + (s % 60) + '秒';
		},
		dotStyle(c) {
			const vp = this.heatmapDialog.viewportW || 375;
			const ph = this.heatmapDialog.pageH || 2000;
			const left = Math.min(100, Math.max(0, (c.x / vp * 100))).toFixed(2);
			const top = Math.min(100, Math.max(0, (c.y / ph * 100))).toFixed(2);
			return { left: left + '%', top: top + '%' };
		}
	},
	mounted() {
		this.loadList();
		this.loadPerm();
		this.loadTrackLinks();
	}
};
</script>

<style scoped lang="scss">
.rs-page {
	height: 100%;
	overflow-y: auto;
	padding: 20px 28px;
	box-sizing: border-box;
	text-align: left;
	color: #333;

	.rs-track-section {
		margin-top: 24px;
		padding-top: 20px;
		border-top: 1px solid #ebeef5;
	}

	.rs-track-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 14px;

		.rs-track-tabs {
			display: flex;
			gap: 4px;
		}

		.rs-track-tab {
			padding: 4px 14px;
			font-size: 14px;
			font-weight: 600;
			cursor: pointer;
			color: #909399;
			border-bottom: 2px solid transparent;
			transition: all .2s;

			&.active {
				color: #409eff;
				border-bottom-color: #409eff;
			}

			&:hover {
				color: #409eff;
			}
		}

		.rs-track-title {
			font-size: 16px;
			font-weight: 600;
		}

		.rs-track-actions {
			display: flex;
			align-items: center;
			gap: 8px;
		}
	}

	.rs-track-url {
		font-size: 12px;
		color: #409eff;
		word-break: break-all;
	}

	.rs-track-hit {
		color: #e6a23c;
		font-weight: 600;
	}

	.rs-track-tip {
		margin-top: 10px;
		font-size: 12px;
		color: #909399;

		code {
			background: #f5f7fa;
			padding: 2px 6px;
			border-radius: 3px;
			color: #e6a23c;
			font-size: 12px;
		}
	}

	.rs-track-preview {
		margin-top: 4px;
		font-size: 12px;
		color: #e6a23c;
	}

	.rs-track-empty {
		padding: 40px 0;
		text-align: center;
		color: #909399;
		font-size: 14px;
	}

	/* ====== 设备库展开子表格 ====== */
	.rs-dev-sub-header {
		background: #f8f9fb !important;
		font-size: 12px;
		color: #909399;
	}
	.rs-dev-card-icon {
		font-size: 14px;
	}

	/* ====== UA详情弹窗 ====== */
	.rs-ua-dialog .el-dialog__body {
		padding: 12px 20px;
	}
	.rs-ua-body {
		padding: 4px 0;
	}
	.rs-ua-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1px;
		background: #ebeef5;
		border: 1px solid #ebeef5;
		border-radius: 6px;
		overflow: hidden;
		margin-bottom: 16px;
	}
	.rs-ua-cell {
		background: #fff;
		padding: 10px 14px;
	}
	.rs-ua-cell-wide {
		grid-column: 1 / -1;
	}
	.rs-ua-label {
		font-size: 12px;
		color: #909399;
		margin-bottom: 4px;
	}
	.rs-ua-value {
		font-size: 14px;
		color: #303133;
		font-weight: 500;
	}
	.rs-ua-mono {
		font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
	}
	.rs-ua-raw {
		border-top: 1px solid #ebeef5;
		padding-top: 14px;
	}
	.rs-ua-raw-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
	}
	.rs-ua-raw-title {
		font-size: 13px;
		font-weight: 600;
		color: #303133;
	}
	.rs-ua-raw-text {
		font-size: 12px;
		color: #606266;
		word-break: break-all;
		line-height: 1.7;
		background: #f5f7fa;
		padding: 12px 14px;
		border-radius: 6px;
		border: 1px solid #e4e7ed;
		font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
	}
	.rs-mark-edit {
		color: #e6a23c;
	}

	/* ====== 设备标记弹窗 ====== */
	.rs-mark-form {
		padding: 4px 0;
	}
	.rs-mark-presets {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	/* ====== 访问明细标记 ====== */
	.rs-visit-nomark {
		color: #c0c4cc;
	}

	.rs-visit-summary {
		margin-bottom: 12px;
		font-size: 13px;

		code {
			background: #f5f7fa;
			padding: 2px 6px;
			border-radius: 3px;
			color: #409eff;
		}
	}

	.rs-visit-stats {
		margin-left: 16px;
		color: #606266;

		b {
			color: #f56c6c;
			font-size: 15px;
		}
	}

	.rs-visitor-section {
		margin-bottom: 16px;
	}

	.rs-visit-expand {
		padding: 8px 16px;
	}

	.rs-visit-expand-ips {
		margin-bottom: 8px;
		padding-bottom: 8px;
		border-bottom: 1px dashed #e4e7ed;
	}

	.rs-visit-ip-count {
		color: #e6a23c;
		font-size: 11px;
		margin-left: 2px;
	}

	.rs-visit-expand-label {
		font-size: 12px;
		color: #909399;
		margin-right: 8px;
	}

	.rs-visit-time-tag {
		display: inline-block;
		margin: 2px 6px 2px 0;
		padding: 2px 10px;
		background: #ecf5ff;
		border: 1px solid #d9ecff;
		border-radius: 12px;
		font-size: 12px;
		color: #409eff;
	}

	.rs-visitor-title {
		font-size: 13px;
		font-weight: 600;
		color: #303133;
		margin-bottom: 8px;
		padding-left: 8px;
		border-left: 3px solid #409eff;
	}

	.rs-visit-multi {
		color: #f56c6c;
		font-weight: 700;
	}

	.rs-heatmap-toolbar {
		margin-bottom: 12px;
		display: flex;
		align-items: center;
		gap: 20px;
		font-size: 13px;
		color: #606266;
	}

	.rs-heatmap-toolbar b {
		color: #f56c6c;
		font-size: 15px;
		margin-left: 2px;
	}

	.rs-heatmap-legend {
		margin-left: auto;
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 12px;
	}

	.rs-heatmap-legend-hot {
		display: inline-block;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: radial-gradient(circle, rgba(255,0,0,0.7), rgba(255,165,0,0.3));
		border: 1px solid rgba(255,0,0,0.8);
		vertical-align: middle;
		margin-right: 2px;
	}

	.rs-heatmap-legend-fold {
		display: inline-block;
		width: 20px;
		height: 0;
		border-top: 2px dashed #409eff;
		vertical-align: middle;
		margin-right: 2px;
	}

	.rs-heatmap-legend-scroll {
		display: inline-block;
		width: 20px;
		height: 0;
		border-top: 2px solid #f56c6c;
		vertical-align: middle;
		margin-right: 2px;
	}

	.rs-heatmap-scroll {
		max-height: 600px;
		overflow: auto;
		border: 1px solid #dcdfe6;
		border-radius: 6px;
		background: #f0f0f0;
		display: flex;
		justify-content: center;
		padding: 12px;
	}

	.rs-heatmap-page {
		position: relative;
		flex-shrink: 0;
		box-shadow: 0 2px 12px rgba(0,0,0,0.15);
		background: #fff;
	}

	.rs-heatmap-iframe {
		width: 100%;
		height: 100%;
		border: none;
		display: block;
		pointer-events: none;
	}

	.rs-heatmap-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		z-index: 10;
	}

	.rs-heatmap-hot {
		position: absolute;
		width: 36px;
		height: 36px;
		margin-left: -18px;
		margin-top: -18px;
		border-radius: 50%;
		background: radial-gradient(circle, rgba(255,0,0,0.6) 0%, rgba(255,140,0,0.4) 40%, rgba(255,255,0,0.15) 70%, transparent 100%);
		border: 2px solid rgba(255,0,0,0.9);
		z-index: 20;
		cursor: pointer;
		transition: transform 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.rs-heatmap-hot:hover {
		transform: scale(1.5);
		z-index: 30;
	}

	.rs-heatmap-num {
		font-size: 12px;
		font-weight: 700;
		color: #fff;
		text-shadow: 0 0 3px rgba(0,0,0,0.8);
	}

	.rs-heatmap-fold {
		position: absolute;
		left: 0;
		right: 0;
		border-top: 2px dashed #409eff;
		z-index: 15;
	}

	.rs-heatmap-scrollline {
		position: absolute;
		left: 0;
		right: 0;
		border-top: 2px solid #f56c6c;
		z-index: 15;
	}

	.rs-forbidden {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		color: #909399;

		i {
			font-size: 46px;
		}

		.fb-title {
			margin-top: 14px;
			font-size: 18px;
		}

		.fb-tip {
			margin-top: 6px;
			font-size: 13px;
			color: #c0c4cc;
		}
	}

	.rs-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 18px;

		.rs-title {
			font-size: 18px;
			font-weight: 600;
		}

		.rs-actions {
			display: flex;
			align-items: center;

			.rs-code {
				margin-right: 14px;
				font-size: 13px;
				color: #909399;
			}
		}
	}

	.rs-empty {
		padding: 60px 0;
		text-align: center;
		color: #909399;
		font-size: 14px;
	}

	.rs-grid {
		display: grid;
		// 卡片宽度限在 260~340px,配合正方形封面更像海报,单张时不会撑满整行
		grid-template-columns: repeat(auto-fill, minmax(260px, 340px));
		gap: 16px;
	}

	.rs-card {
		border: 1px solid #ebeef5;
		border-radius: 8px;
		overflow: hidden;
		background: #fff;
		transition: box-shadow .2s;

		&:hover {
			box-shadow: 0 4px 16px rgba(0, 0, 0, .08);
		}

		.rs-cover {
			// 正方形封面,随卡片宽度自适应
			aspect-ratio: 1 / 1;
			background-size: cover;
			background-position: center;
			background-color: #f5f7fa;
			display: flex;
			align-items: center;
			justify-content: center;

			.rs-cover-ph i {
				font-size: 42px;
				color: #c0c4cc;
			}
		}

		.rs-info {
			padding: 12px 14px;

			.rs-name {
				font-size: 15px;
				font-weight: 600;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.rs-summary {
				margin-top: 6px;
				font-size: 12px;
				color: #909399;
				line-height: 1.5;
				height: 36px;
				overflow: hidden;
			}

			.rs-link {
				margin-top: 12px;
				display: flex;
				flex-direction: column;
				gap: 8px;

				.el-button {
					width: 100%;
				}
			}
		}
	}

	.rs-wm-tip {
		font-size: 12px;
		color: #909399;
		margin-bottom: 12px;
		line-height: 1.5;
	}

	.rs-wm-current {
		margin-top: 10px;
		font-size: 12px;
		color: #606266;
	}
}
</style>

<style lang="scss">
/* 弹窗均挂在body下(append-to-body),scoped样式无法命中,需用全局样式 */
.rs-wm-tip {
	font-size: 12px;
	color: #909399;
	margin-bottom: 12px;
	line-height: 1.5;
}

.rs-wm-current {
	margin-top: 10px;
	font-size: 12px;
	color: #606266;
}

.rs-dm-row {
	font-size: 13px;
	color: #606266;
	margin-bottom: 12px;

	.rs-dm-ok {
		color: #67c23a;
		font-weight: 600;
	}

	.rs-dm-none {
		color: #c0c4cc;
	}

	.rs-dm-apply {
		margin-left: 6px;
		color: #303133;
	}

	.rs-dm-reason {
		display: block;
		margin-top: 4px;
		color: #f56c6c;
		font-size: 12px;
	}
}

.rs-qr-dialog {
	border-radius: 12px;

	.el-dialog__header {
		text-align: center;
		padding: 20px 20px 8px;

		.el-dialog__title {
			font-size: 16px;
			font-weight: 600;
			color: #303133;
		}
	}

	.el-dialog__body {
		padding: 8px 28px 28px;
	}

	.rs-qr-box {
		display: flex;
		flex-direction: column;
		align-items: center;
		min-height: 320px;
	}

	.rs-qr-frame {
		width: 216px;
		height: 216px;
		padding: 12px;
		border: 1px solid #ebeef5;
		border-radius: 12px;
		background: #fff;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			display: block;
		}
	}

	.rs-qr-name {
		margin-top: 14px;
		max-width: 260px;
		font-size: 14px;
		font-weight: 600;
		color: #303133;
		text-align: center;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.rs-qr-tip {
		margin-top: 8px;
		padding: 8px 12px;
		border-radius: 6px;
		background: #f4f6fa;
		font-size: 12px;
		line-height: 1.6;
		color: #909399;
		text-align: center;

		i {
			margin-right: 2px;
			color: #b0b6c3;
		}
	}

	.rs-qr-download {
		margin-top: 18px;
		width: 100%;
	}
}

.rs-manage-dialog {
	.rs-mg-toolbar {
		margin-bottom: 12px;
		display: flex;
		align-items: center;
	}

	.rs-mg-thumb {
		width: 40px;
		height: 40px;
		object-fit: cover;
		border-radius: 4px;
		display: block;
		margin: 0 auto;
	}

	.rs-mg-danger {
		color: #f56c6c;
	}
}

.rs-edit-dialog {
	.el-form-item {
		text-align: left;
	}

	.rs-edit-ok {
		margin-left: 10px;
		font-size: 12px;
		color: #67c23a;
	}

	.rs-edit-preview-row {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.rs-edit-img {
		margin-top: 8px;
		max-width: 220px;
		max-height: 160px;
		border: 1px solid #ebeef5;
		border-radius: 4px;
		display: block;
	}

	.rs-edit-cover {
		max-width: 120px;
		max-height: 120px;
	}
}
</style>
